import React from "react";
import { Clock, Mail, MessageCircle, Settings2 } from "lucide-react";
import { fmt } from "../../../../services/erpApi";
import { AVATAR_BG, getInitials, ODOO } from "./crmV2Helpers";

const th = {
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 700,
  color: ODOO.muted,
  textAlign: "left",
  borderBottom: `1px solid ${ODOO.border}`,
  background: "#f8f9fa",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const td = {
  padding: "7px 10px",
  fontSize: 13,
  color: ODOO.text,
  borderBottom: `1px solid ${ODOO.borderLight}`,
  whiteSpace: "nowrap",
  maxWidth: 180,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const CrmV2ListView = ({
  leads,
  total,
  loading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  stageNameOf,
  userNameOf,
  onRowClick,
  onStubAction,
}) => {
  const allSelected = leads.length > 0 && leads.every((l) => selectedIds.includes(l.id));
  const sum = leads.reduce((s, l) => s + (Number(l.expected_revenue) || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 200px)", background: "#fff" }}>
      <div style={{ flex: 1, overflow: "auto" }}>
        {loading && leads.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: ODOO.muted, fontSize: 13 }}>Loading…</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={onToggleSelectAll} />
                </th>
                <th style={th}>Opportunity</th>
                <th style={th}>Contact Name</th>
                <th style={th}>Email</th>
                <th style={th}>Phone</th>
                <th style={th}>Company</th>
                <th style={th}>Salesperson</th>
                <th style={th}>Activities</th>
                <th style={th}>My Deadline</th>
                <th style={{ ...th, textAlign: "right" }}>Expected Revenue</th>
                <th style={th}>Stage</th>
                <th style={th}>Groups</th>
                <th style={{ ...th, width: 70 }} />
                <th style={{ ...th, width: 36 }}>
                  <Settings2 size={14} style={{ color: ODOO.muted }} />
                </th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={14} style={{ ...td, textAlign: "center", padding: 32, color: ODOO.muted }}>
                    No records found
                  </td>
                </tr>
              ) : (
                leads.map((l, idx) => {
                  const salesperson = userNameOf?.(l.user_id) || l.user_name || "";
                  const stage = stageNameOf?.(l.stage_id) || l.stage_name || "—";
                  return (
                    <tr
                      key={l.id}
                      onClick={() => onRowClick?.(l)}
                      style={{
                        background: selectedIds.includes(l.id) ? ODOO.primarySoft : idx % 2 === 0 ? "#fff" : "#fafbfc",
                        cursor: "pointer",
                      }}
                    >
                      <td style={td} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(l.id)}
                          onChange={() => onToggleSelect(l.id)}
                        />
                      </td>
                      <td style={{ ...td, fontWeight: 600, maxWidth: 220 }}>{l.name || "—"}</td>
                      <td style={td}>{l.contact_name || l.partner_name || "—"}</td>
                      <td style={td}>{l.email_from || l.email || "—"}</td>
                      <td style={td}>{l.phone || l.mobile || "—"}</td>
                      <td style={td}>{l.partner_name || l.company || "—"}</td>
                      <td style={td}>
                        {salesperson ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: AVATAR_BG[(Number(l.user_id) || 0) % AVATAR_BG.length],
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 8,
                                fontWeight: 700,
                                color: "#fff",
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(salesperson)}
                            </span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{salesperson}</span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={td}>
                        {l.next_activity ? <Clock size={14} style={{ color: ODOO.muted }} /> : ""}
                      </td>
                      <td style={td}>{l.date_deadline ? fmt.date(l.date_deadline) : ""}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt.money(l.expected_revenue)}</td>
                      <td style={td}>{stage}</td>
                      <td style={td}>
                        {Array.isArray(l.group_names) && l.group_names.length > 0 ? (
                          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                            {l.group_names.map((name) => (
                              <span key={name} style={{ background: "#eef2f7", color: "#334155", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>{name}</span>
                            ))}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={td} onClick={(e) => e.stopPropagation()}>
                        <a href={l.email_from || l.email ? `mailto:${l.email_from || l.email}` : undefined} onClick={(e) => { e.preventDefault(); onStubAction?.("email", l); }} title="Email — send via CRM" style={{ border: "none", background: "transparent", cursor: "pointer", color: ODOO.primary, padding: 2, display: "inline-flex" }}>
                          <Mail size={14} />
                        </a>
                        <button
                          type="button"
                          title="WhatsApp — send via CRM"
                          onClick={() => onStubAction?.("whatsapp", l)}
                          style={{ border: "none", background: "transparent", cursor: "pointer", color: ODOO.primary, padding: 2 }}
                        >
                          <MessageCircle size={14} />
                        </button>
                      </td>
                      <td style={td} />
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer summary bar */}
      <div
        style={{
          flexShrink: 0,
          background: ODOO.footerBg,
          color: "#fff",
          padding: "8px 16px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 24,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <span style={{ opacity: 0.85 }}>{total} record{total !== 1 ? "s" : ""}</span>
        <span>{fmt.money(sum)}</span>
      </div>
    </div>
  );
};

export default CrmV2ListView;
